import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage, getStatusLabel } from '../contexts/LanguageContext';
import { Package, MagnifyingGlass, MapPin, Clock, CheckCircle, Airplane, Translate, ArrowLeft, User } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusSteps = [
  { key: 'intake_miami', icon: MapPin, label_en: 'Intake Miami', label_es: 'Recibido Miami' },
  { key: 'in_transit', icon: Airplane, label_en: 'In Transit', label_es: 'En Tránsito' },
  { key: 'arrived_san_jose', icon: MapPin, label_en: 'Arrived San José', label_es: 'Llegó San José' },
  { key: 'ready_for_pickup', icon: Package, label_en: 'Ready for Pickup', label_es: 'Listo para Recoger' },
  { key: 'delivered', icon: CheckCircle, label_en: 'Delivered', label_es: 'Entregado' }
];

export default function TrackingPage() {
  const { trackingNumber: urlTrackingNumber } = useParams();
  const { tx } = useLanguage();
  const [language, setLanguage] = useState('es');
  const [trackingNumber, setTrackingNumber] = useState(urlTrackingNumber || '');
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const toggleLanguage = () => setLanguage(prev => prev === 'en' ? 'es' : 'en');

  const handleTrack = async (e) => {
    e?.preventDefault();
    if (!trackingNumber.trim()) {
      toast.error(tx('Enter a tracking number', 'Ingresa un número de tracking', 'Enter a tracking number'));
      return;
    }
    
    setLoading(true);
    setSearched(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/packages/track/${trackingNumber.trim()}`);
      setPkg(response.data);
    } catch (error) {
      setPkg(null);
      toast.error(tx('Package not found', 'Paquete no encontrado', 'Package not found'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusStep = (status) => {
    const index = statusSteps.findIndex(s => s.key === status);
    return index >= 0 ? index : 0;
  };

  return (
    <div className="min-h-screen ship-gradient" data-testid="tracking-page">
      {/* Header */}
      <header className="px-6 py-4 flex items-center justify-between">
        <Link to="/login" className="flex items-center gap-2 text-white">
          <img src="/assets/trackmypackage-logo.png" alt="Track My Package" className="h-20" />
        </Link>
        <div className="flex items-center gap-2">
          <Link
            to="/portal"
            className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 bg-white text-[#0077FF] font-semibold rounded-lg hover:bg-white/90 transition-colors shadow-md"
            data-testid="tracking-client-login-btn"
          >
            <User size={16} weight="bold" />
            {language === 'es' ? 'Portal del Cliente' : 'Client Login'}
          </Link>
          <Link
            to="/portal"
            className="sm:hidden inline-flex items-center justify-center w-10 h-10 bg-white text-[#0077FF] rounded-lg shadow-md"
            data-testid="tracking-client-login-btn-mobile"
            title={language === 'es' ? 'Portal del Cliente' : 'Client Login'}
          >
            <User size={18} weight="bold" />
          </Link>
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-2 px-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg"
          >
            <Translate size={18} />
            <span className="text-sm hidden sm:inline">{language === 'en' ? 'Español' : 'English'}</span>
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {/* Search Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <h1 
            className="text-4xl md:text-5xl font-bold text-white tracking-wide mb-4"
            style={{ fontFamily: 'Bebas Neue' }}
          >
            {tx('Track Your Package', 'Rastrea Tu Paquete', 'Track Your Package')}
          </h1>
          <p className="text-white/70 mb-8">
            {tx('Enter your tracking number to see the status', 'Ingresa tu número de tracking para ver el estado', 'Enter your tracking number to see the status')}
          </p>

          <form onSubmit={handleTrack} className="flex gap-3 max-w-xl mx-auto">
            <div className="relative flex-1">
              <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value.toUpperCase())}
                placeholder={tx('E.g. WB260315123456789', 'Ej: WB260315123456789', 'E.g. WB260315123456789')}
                className="pl-12 h-14 rounded-xl text-lg font-mono bg-white"
                data-testid="tracking-input"
              />
            </div>
            <Button 
              type="submit"
              disabled={loading}
              className="bg-[#FFB300] hover:bg-[#FFB300]/90 text-gray-900 rounded-xl h-14 px-8 font-bold"
              data-testid="track-btn"
            >
              {loading ? '...' : (tx('Search', 'Buscar', 'Rechercher'))}
            </Button>
          </form>
        </motion.div>

        {/* Results */}
        {searched && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {pkg ? (
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                {/* Tracking Header */}
                <div className="ship-gradient text-white p-6">
                  <p className="text-xs uppercase tracking-wide text-white/70 mb-1">Tracking #</p>
                  <p className="text-2xl font-mono font-bold">{pkg.tracking_number}</p>
                </div>

                {/* Status Timeline */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    {statusSteps.map((step, index) => {
                      const currentStep = getStatusStep(pkg.status);
                      const isCompleted = index <= currentStep;
                      const isCurrent = index === currentStep;
                      const Icon = step.icon;
                      
                      return (
                        <div key={step.key} className="flex flex-col items-center relative flex-1">
                          {index > 0 && (
                            <div 
                              className={`absolute right-1/2 top-5 h-0.5 w-full ${
                                index <= currentStep ? 'bg-[#0077FF]' : 'bg-gray-200'
                              }`}
                            />
                          )}
                          <div 
                            className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center ${
                              isCompleted 
                                ? isCurrent 
                                  ? 'bg-[#0077FF] ring-4 ring-[#0077FF]/20' 
                                  : 'bg-[#0077FF]'
                                : 'bg-gray-200'
                            }`}
                          >
                            <Icon size={20} className={isCompleted ? 'text-white' : 'text-gray-400'} weight={isCompleted ? 'fill' : 'regular'} />
                          </div>
                          <p className={`text-xs mt-2 text-center max-w-16 ${isCurrent ? 'font-bold text-[#0077FF]' : 'text-gray-500'}`}>
                            {language === 'fr' ? (step.label_fr || step.label_en) : language === 'es' ? step.label_es : step.label_en}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Package Details */}
                <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                      {tx('Description', 'Descripción', 'Description')}
                    </p>
                    <p className="font-medium">{pkg.description}</p>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                      {tx('Weight', 'Peso', 'Poids')}
                    </p>
                    <p className="font-medium">{pkg.weight_lbs} lbs / {pkg.weight_kg} kg</p>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                      {tx('Shipping Cost', 'Costo de Envío', 'Shipping Cost')}
                    </p>
                    <p className="text-xl font-bold text-[#0077FF]">${pkg.shipping_cost}</p>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                      {tx('Current Status', 'Estado Actual', 'Current Status')}
                    </p>
                    <span className={`status-badge status-${pkg.status}`}>
                      {getStatusLabel(pkg.status, language)}
                    </span>
                  </div>
                </div>

                {/* Timeline Events */}
                <div className="p-6 bg-gray-50 border-t">
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-3">
                    {tx('Timeline', 'Historial', 'Timeline')}
                  </p>
                  <div className="space-y-3">
                    {pkg.intake_date && (
                      <div className="flex items-center gap-3 text-sm">
                        <div className="w-2 h-2 bg-blue-500 rounded-full" />
                        <span className="text-gray-600">
                          {tx('Received in Miami', 'Recibido en Miami', 'Received in Miami')}: {new Date(pkg.intake_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {pkg.in_transit_date && (
                      <div className="flex items-center gap-3 text-sm">
                        <div className="w-2 h-2 bg-amber-500 rounded-full" />
                        <span className="text-gray-600">
                          {tx('On the way to San José', 'En camino a San José', 'On the way to San José')}: {new Date(pkg.in_transit_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {pkg.arrived_date && (
                      <div className="flex items-center gap-3 text-sm">
                        <div className="w-2 h-2 bg-teal-500 rounded-full" />
                        <span className="text-gray-600">
                          {tx('Arrived in San José', 'Llegó a San José', 'Arrived in San José')}: {new Date(pkg.arrived_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {pkg.ready_date && (
                      <div className="flex items-center gap-3 text-sm">
                        <div className="w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-gray-600">
                          {tx('Ready for pickup', 'Listo para recoger', 'Ready for pickup')}: {new Date(pkg.ready_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {pkg.delivered_date && (
                      <div className="flex items-center gap-3 text-sm">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                        <span className="text-gray-600">
                          {tx('Delivered', 'Entregado', 'Livré')}: {new Date(pkg.delivered_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                <Package size={64} className="mx-auto mb-4 text-gray-300" />
                <h2 className="text-xl font-semibold text-gray-700 mb-2">
                  {tx('Package Not Found', 'Paquete No Encontrado', 'Package Not Found')}
                </h2>
                <p className="text-gray-500">
                  {language === 'es' 
                    ? `No encontramos un paquete con tracking "${trackingNumber}"` 
                    : `We couldn't find a package with tracking "${trackingNumber}"`}
                </p>
              </div>
            )}
          </motion.div>
        )}

        {/* Contact Info */}
        <div className="text-center mt-10 text-white/60 text-sm">
          <p>trackingsystem.app</p>
          <p>info@trackingsystem.app</p>
        </div>
      </main>
    </div>
  );
}
